import pluralize from 'pluralize'
import { deepEquals } from '@rjsf/utils'
import { isPlainObject, type JsonObject } from '../mergePatch'

export interface ListConfig {
  layout: 'expandable' | 'rows'
  noun: string
  emptyText: string
  itemTitle: string
}

export const isArraySchema = (schema: unknown): schema is JsonObject => isPlainObject(schema) && schema.type === 'array'

export const objectItemsOf = (schema: unknown): JsonObject | null =>
  isArraySchema(schema) && isPlainObject(schema.items) && schema.items.type === 'object' ? schema.items : null

const propertiesOf = (schema: unknown): JsonObject => (isPlainObject(schema) && isPlainObject(schema.properties) ? schema.properties : {})

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

const isStructuredField = (field: unknown): boolean => isPlainObject(field) && [field.type].flat().some((type) => type === 'object' || type === 'array')

export const listConfigOf = (schema: unknown): ListConfig => {
  const items = isArraySchema(schema) && isPlainObject(schema.items) ? schema.items : {}
  const objectItems = objectItemsOf(schema)
  const itemTitle = typeof items.title === 'string' && items.title ? items.title : 'item'
  const noun = itemTitle.toLowerCase()
  return {
    layout: objectItems ? 'expandable' : 'rows',
    noun,
    emptyText: `No ${pluralize(noun)} yet.`,
    itemTitle: capitalize(itemTitle),
  }
}

export const summaryFieldOf = (itemsSchema: unknown, order: unknown): string | null => {
  const properties = propertiesOf(itemsSchema)
  const ordered = Array.isArray(order) ? order.filter((name): name is string => typeof name === 'string') : []
  return [...ordered, ...Object.keys(properties)].find((name) => Object.hasOwn(properties, name) && !isStructuredField(properties[name])) ?? null
}

export const childCountSummary = (itemsSchema: unknown, item: unknown): string | null => {
  const properties = propertiesOf(itemsSchema)
  const entry = Object.entries(properties).find(([, field]) => isArraySchema(field))
  if (!entry) {
    return null
  }
  const [key, field] = entry
  const children = isPlainObject(item) ? item[key] : undefined
  return pluralize(listConfigOf(field).noun, Array.isArray(children) ? children.length : 0, true)
}

export const choiceTitleOf = (fieldSchema: unknown, value: unknown): string | undefined => {
  if (!isPlainObject(fieldSchema) || !Array.isArray(fieldSchema.oneOf)) {
    return undefined
  }
  const match = fieldSchema.oneOf.find((option) => isPlainObject(option) && deepEquals(option.const, value))
  return isPlainObject(match) && typeof match.title === 'string' ? match.title : undefined
}

export const hasErrors = (node: unknown): boolean =>
  isPlainObject(node) && Object.entries(node).some(([key, value]) => (key === '__errors' ? Array.isArray(value) && value.length > 0 : hasErrors(value)))
